/**
 * Where a sign-in lands.
 *
 * This lived inside AuthCallback.jsx, which is a component file no test
 * can import, so the only way to check it was to match its source text.
 * That is what let the bug below survive a fix and a test written for
 * it: the sanitizer's fallback was corrected to the shared landing
 * path, a test asserted the corrected line, and the caller one line
 * away still ended its `||` chain in a literal '/deals' — so the
 * sanitizer never received an empty value and its fallback could never
 * fire. Signing in with Google kept arriving on the Deal Tracker,
 * underneath a comment explaining why it no longer would.
 *
 * Nothing here names a landing page. There is one, and it lives in
 * accessControl.
 */
import { DEFAULT_APP_PATH } from './accessControl.js';

/**
 * A stored path must still be a path on this app: anything without a
 * leading slash, or with two, could be an absolute URL somewhere else,
 * and the login page and the callback itself are both loops.
 */
export function safeNextPath(raw) {
  const value = String(raw || '').trim();
  if (!value || !value.startsWith('/') || value.startsWith('//')) return DEFAULT_APP_PATH;
  if (value.startsWith('/login')) return DEFAULT_APP_PATH;
  if (value.startsWith('/auth/callback')) return DEFAULT_APP_PATH;
  return value;
}

/**
 * The whole resolution, in one place: the ?next= on the callback URL,
 * then whatever was stored before the round trip, then the app's own
 * landing page — supplied by safeNextPath, never written here.
 */
export function resolveNextPath({ paramNext = '', storedNext = '' } = {}) {
  return safeNextPath(paramNext || storedNext);
}
